import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI, groupAPI } from '../services/api';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

function Students() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Initialize state from URL parameters
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [appliedSearch, setAppliedSearch] = useState(searchParams.get('search') || '');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    email: '',
    password: '',
    groupId: '',
    sendEmail: true
  });
  const [creating, setCreating] = useState(false);
  const [currentPage, setCurrentPage] = useState(parseInt(searchParams.get('page')) || 1);
  const [pagination, setPagination] = useState(null);
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') || 'createdAt');
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [studentsPerPage] = useState(10);

  useEffect(() => {
    if (user && user.role !== 'admin') {
      showToast('Access denied. Admin only.', 'error');
      navigate('/user');
    }
  }, [user, navigate, showToast]);

  // Update URL parameters when state changes
  useEffect(() => {
    const params = {};
    if (currentPage > 1) params.page = currentPage.toString();
    if (appliedSearch) params.search = appliedSearch;
    if (sortBy !== 'createdAt') params.sortBy = sortBy;
    if (sortOrder !== 'desc') params.sortOrder = sortOrder;
    
    setSearchParams(params, { replace: true });
  }, [currentPage, appliedSearch, sortBy, sortOrder, setSearchParams]);

  useEffect(() => {
    fetchStudents();
    fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, appliedSearch, sortBy, sortOrder]);

  const handleSearch = () => {
    setAppliedSearch(searchTerm);
    setCurrentPage(1); // Reset to first page on search
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await groupAPI.getMyGroups();
      setGroups(response.data.groups || []);
    } catch (error) {
      console.error('Failed to fetch groups:', error);
      setGroups([]);
    }
  };

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await authAPI.getAllStudents(
        currentPage,
        studentsPerPage,
        appliedSearch,
        sortBy,
        sortOrder
      );
      setStudents(response.data.students);
      setPagination(response.data.pagination);
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to fetch students', 'error');
      if (error.response?.status === 403) {
        navigate('/user');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default desc order
      setSortBy(field);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const response = await authAPI.createUser(createFormData);
      showToast(response.data.message || 'User created successfully', 'success');
      
      // If password was returned (email wasn't sent), show it to admin
      if (response.data.tempPassword) {
        setTimeout(() => {
          alert(`User created successfully!\n\nGenerated Password: ${response.data.tempPassword}\n\nPlease save this password and share it with the user manually.`);
        }, 500);
      }
      
      setShowCreateModal(false);
      setCreateFormData({ name: '', email: '', password: '', groupId: '', sendEmail: true });
      fetchStudents(); // Refresh the list
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="card">
          {/* Header Skeleton */}
          <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <div style={{
                width: '120px',
                height: '24px',
                backgroundColor: '#e2e8f0',
                borderRadius: '6px',
                marginBottom: '8px',
                animation: 'pulse 1.5s ease-in-out infinite'
              }} />
              <div style={{
                width: '160px',
                height: '16px',
                backgroundColor: '#e2e8f0',
                borderRadius: '6px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: '0.1s'
              }} />
            </div>
            <div style={{
              width: '140px',
              height: '44px',
              backgroundColor: '#e2e8f0',
              borderRadius: '8px',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: '0.2s'
            }} />
          </div>

          {/* Search and Filters Skeleton */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{
              flex: '1',
              minWidth: '250px',
              height: '46px',
              backgroundColor: '#e2e8f0',
              borderRadius: '8px',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: '0.3s'
            }} />
            <div style={{
              width: '220px',
              height: '46px',
              backgroundColor: '#e2e8f0',
              borderRadius: '8px',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: '0.4s'
            }} />
            <div style={{
              width: '140px',
              height: '46px',
              backgroundColor: '#e2e8f0',
              borderRadius: '8px',
              animation: 'pulse 1.5s ease-in-out infinite',
              animationDelay: '0.5s'
            }} />
          </div>

          {/* Table Skeleton */}
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  {[40, 150, 200, 150, 140, 100].map((width, i) => (
                    <th key={i} style={{ padding: '12px' }}>
                      <div style={{
                        width: `${width}px`,
                        height: '16px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${0.6 + i * 0.05}s`
                      }} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5, 6, 7, 8].map((row) => (
                  <tr key={row} style={{ borderBottom: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px' }}>
                      <div style={{
                        width: '24px',
                        height: '16px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${0.9 + row * 0.1}s`
                      }} />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{
                        width: '120px',
                        height: '16px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${0.9 + row * 0.1 + 0.05}s`
                      }} />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{
                        width: '180px',
                        height: '16px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${0.9 + row * 0.1 + 0.1}s`
                      }} />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {[60, 80].map((width, i) => (
                          <div key={i} style={{
                            width: `${width}px`,
                            height: '22px',
                            backgroundColor: '#e2e8f0',
                            borderRadius: '12px',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            animationDelay: `${0.9 + row * 0.1 + 0.15 + i * 0.05}s`
                          }} />
                        ))}
                      </div>
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{
                        width: '100px',
                        height: '16px',
                        backgroundColor: '#e2e8f0',
                        borderRadius: '4px',
                        margin: '0 auto',
                        animation: 'pulse 1.5s ease-in-out infinite',
                        animationDelay: `${0.9 + row * 0.1 + 0.25}s`
                      }} />
                    </td>
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {[32, 32].map((size, i) => (
                          <div key={i} style={{
                            width: `${size}px`,
                            height: `${size}px`,
                            backgroundColor: '#e2e8f0',
                            borderRadius: '6px',
                            animation: 'pulse 1.5s ease-in-out infinite',
                            animationDelay: `${0.9 + row * 0.1 + 0.3 + i * 0.05}s`
                          }} />
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Skeleton */}
          <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px' }}>
            {[60, 36, 36, 36, 60].map((width, i) => (
              <div key={i} style={{
                width: `${width}px`,
                height: '36px',
                backgroundColor: '#e2e8f0',
                borderRadius: '6px',
                animation: 'pulse 1.5s ease-in-out infinite',
                animationDelay: `${1.8 + i * 0.05}s`
              }} />
            ))}
          </div>

          <style>{`
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.5;
              }
            }
          `}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>Students</h2>
            <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
              {pagination ? `${pagination.totalStudents} total students` : ''}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5568d3'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
          >
            <span style={{ fontSize: '18px' }}>+</span> Create User
          </button>
        </div>

        <div style={{ marginBottom: '20px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="🔍 Search by name or email... (Press Enter)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            style={{
              flex: '1',
              minWidth: '250px',
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => e.target.style.borderColor = '#667eea'}
            onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
          />
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              padding: '12px 16px',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="createdAt">Sort by: Registration Date</option>
            <option value="name">Sort by: Name</option>
            <option value="email">Sort by: Email</option>
          </select>
          <button
            onClick={() => {
              setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              setCurrentPage(1);
            }}
            style={{
              padding: '12px 16px',
              backgroundColor: '#f7fafc',
              border: '1.5px solid #e2e8f0',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            {sortOrder === 'asc' ? '↑' : '↓'} {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        </div>

        {students.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: '#718096', fontSize: '14px' }}>
            <p style={{ marginBottom: '8px', fontSize: '15px' }}>
              {appliedSearch ? 'No students found matching your search' : 'No students registered yet'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f7fafc', borderBottom: '2px solid #e2e8f0' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2d3748' }}>
                    #
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2d3748' }}>
                    Name
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2d3748' }}>
                    Email
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#2d3748' }}>
                    Groups
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#2d3748' }}>
                    Registered On
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px', fontWeight: '700', color: '#2d3748' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, index) => (
                  <tr 
                    key={student._id}
                    style={{ 
                      borderBottom: '1px solid #e2e8f0',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f7fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '16px 12px', fontSize: '13px', color: '#718096', fontWeight: '600' }}>
                      {(currentPage - 1) * studentsPerPage + index + 1}
                    </td>
                    <td style={{ padding: '16px 12px', fontSize: '14px', color: '#2d3748', fontWeight: '600', textTransform: 'capitalize' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {student.name}
                        {student.isActive === false && (
                          <span style={{
                            padding: '3px 8px',
                            backgroundColor: '#fef2f2',
                            color: '#dc2626',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            border: '1px solid #fecaca'
                          }}>
                            Deactivated
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding: '16px 12px', fontSize: '13px', color: '#4a5568' }}>
                      {student.email}
                    </td>
                    <td style={{ padding: '16px 12px', fontSize: '13px' }}>
                      {student.groups && student.groups.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {student.groups.map(group => (
                            <span
                              key={group._id}
                              style={{
                                padding: '4px 10px',
                                backgroundColor: '#eef2ff',
                                color: '#667eea',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                border: '1px solid #c7d2fe'
                              }}
                            >
                              {group.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span style={{ color: '#a0aec0', fontSize: '12px', fontStyle: 'italic' }}>
                          No groups
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center', fontSize: '12px', color: '#718096' }}>
                      {new Date(student.createdAt).toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric', 
                        year: 'numeric' 
                      })}
                    </td>
                    <td style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => navigate(`/students/${student._id}`)}
                          style={{
                            padding: '6px 14px',
                            backgroundColor: '#667eea',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#5568d3'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#667eea'}
                        >
                          View Profile
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {pagination && pagination.totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#f7fafc',
            borderRadius: '8px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <div style={{ fontSize: '13px', color: '#718096' }}>
              Showing {(currentPage - 1) * studentsPerPage + 1} to{' '}
              {Math.min(currentPage * studentsPerPage, pagination.totalStudents)} of{' '}
              {pagination.totalStudents} students
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 1 ? '#e2e8f0' : 'white',
                  color: currentPage === 1 ? '#a0aec0' : '#667eea',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                }}
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={!pagination.hasPrevPage}
                style={{
                  padding: '8px 12px',
                  backgroundColor: !pagination.hasPrevPage ? '#e2e8f0' : 'white',
                  color: !pagination.hasPrevPage ? '#a0aec0' : '#667eea',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: !pagination.hasPrevPage ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </button>
              <div style={{
                padding: '8px 16px',
                backgroundColor: '#667eea',
                color: 'white',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600'
              }}>
                Page {currentPage} of {pagination.totalPages}
              </div>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!pagination.hasNextPage}
                style={{
                  padding: '8px 12px',
                  backgroundColor: !pagination.hasNextPage ? '#e2e8f0' : 'white',
                  color: !pagination.hasNextPage ? '#a0aec0' : '#667eea',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: !pagination.hasNextPage ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </button>
              <button
                onClick={() => setCurrentPage(pagination.totalPages)}
                disabled={currentPage === pagination.totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === pagination.totalPages ? '#e2e8f0' : 'white',
                  color: currentPage === pagination.totalPages ? '#a0aec0' : '#667eea',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: currentPage === pagination.totalPages ? 'not-allowed' : 'pointer'
                }}
              >
                Last
              </button>
            </div>
          </div>
        )}

        {/* Create User Modal */}
        {showCreateModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={() => !creating && setShowCreateModal(false)}
          >
            <div 
              style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '32px',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '90vh',
                overflowY: 'auto',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 style={{ 
                fontSize: '20px', 
                fontWeight: '700', 
                color: '#1a202c',
                marginBottom: '8px'
              }}>
                Create New User
              </h3>
              <p style={{ 
                color: '#718096', 
                fontSize: '14px', 
                marginBottom: '24px' 
              }}>
                Create a user account and optionally assign to a group.
              </p>

              <form onSubmit={handleCreateUser}>
                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    value={createFormData.name}
                    onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
                    required
                    placeholder="Enter full name"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Email *
                  </label>
                  <input
                    type="email"
                    value={createFormData.email}
                    onChange={(e) => setCreateFormData({ ...createFormData, email: e.target.value })}
                    required
                    placeholder="user@example.com"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Password (optional)
                  </label>
                  <input
                    type="text"
                    value={createFormData.password}
                    onChange={(e) => setCreateFormData({ ...createFormData, password: e.target.value })}
                    placeholder="Leave empty to auto-generate"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#718096', 
                    marginTop: '6px',
                    marginBottom: 0
                  }}>
                    If left empty, a random password will be generated
                  </p>
                </div>

                <div className="form-group" style={{ marginBottom: '16px' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px',
                    color: '#374151',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    Assign to Group (optional)
                  </label>
                  <select
                    value={createFormData.groupId}
                    onChange={(e) => setCreateFormData({ ...createFormData, groupId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">No group</option>
                    {groups.map(group => (
                      <option key={group._id} value={group._id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group" style={{ marginBottom: '24px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#374151',
                    fontWeight: '500'
                  }}>
                    <input
                      type="checkbox"
                      checked={createFormData.sendEmail}
                      onChange={(e) => setCreateFormData({ ...createFormData, sendEmail: e.target.checked })}
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer'
                      }}
                    />
                    Send login credentials via email
                  </label>
                  <p style={{ 
                    fontSize: '12px', 
                    color: '#718096', 
                    marginTop: '6px',
                    marginBottom: 0,
                    marginLeft: '26px'
                  }}>
                    {createFormData.sendEmail 
                      ? 'User will receive their login credentials via email'
                      : 'Password will be displayed after creation. You must share it manually with the user.'}
                  </p>
                </div>

                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '20px'
                }}>
                  <p style={{ 
                    fontSize: '13px', 
                    color: '#166534',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    ✓ User will be automatically approved and verified<br/>
                    {createFormData.sendEmail && '✓ Login credentials will be sent via email'}<br/>
                    {createFormData.groupId && '✓ User will be added to the selected group'}
                  </p>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    disabled={creating}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#e5e7eb',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: creating ? 'not-allowed' : 'pointer',
                      opacity: creating ? 0.6 : 1
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: creating ? '#9ca3af' : '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: creating ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {creating ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Students;
